'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';

export type UserRole = 'super_admin' | 'admin_it' | 'tecnico' | 'operatore' | 'sola_lettura';

interface RoleGuardProps {
  /** Minimum roles allowed to access this page */
  allowedRoles: UserRole[];
  children: React.ReactNode;
  /** Where to redirect on access denied (default: /network-dashboard) */
  redirectTo?: string;
}

/**
 * RoleGuard — wraps page content and enforces role-based access.
 * - While auth is loading → shows a spinner
 * - If user has no profile yet → waits (profile loads async after session)
 * - If user's role is not in allowedRoles → redirects to redirectTo
 */
export default function RoleGuard({ allowedRoles, children, redirectTo = '/network-dashboard' }: RoleGuardProps) {
  const { profile, loading } = useAuth();
  const router = useRouter();

  useEffect(() => {
    if (loading) return;
    if (!profile) return; // still loading profile
    if (!allowedRoles.includes(profile.role as UserRole)) {
      router.replace(redirectTo);
    }
  }, [loading, profile, allowedRoles, redirectTo, router]);

  // Still loading auth state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-accent-color border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-color text-sm">Verifica accesso…</p>
        </div>
      </div>
    );
  }

  // Profile loaded but role not allowed
  if (profile && !allowedRoles.includes(profile.role as UserRole)) {
    return (
      <div className="flex items-center justify-center h-full min-h-[60vh]">
        <div className="flex flex-col items-center gap-4 text-center px-6">
          <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center">
            <svg className="w-8 h-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white mb-1">Accesso negato</h2>
            <p className="text-muted-color text-sm">
              Non hai i permessi necessari per visualizzare questa pagina.
            </p>
            <p className="text-muted-color text-xs mt-1">
              Ruolo richiesto: <span className="text-accent-color">{allowedRoles.join(', ')}</span>
            </p>
          </div>
          <button
            onClick={() => router.replace(redirectTo)}
            className="px-4 py-2 bg-accent-color text-white rounded-lg text-sm hover:opacity-90 transition-opacity"
          >
            Torna alla Dashboard
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
